/**
 * Print headers and footers that show the loaded project and estimate names on every sheet.
 *
 * Ediphi staff put `{{project.name}}` or `{{estimate.name}}` into the active sheet's header or
 * footer and save it. Each of the six slots (left, center or right of the header or footer) that
 * holds a token becomes the workbook's template for that slot. Whenever the project or estimate
 * report row is written or cleared, every sheet's templated slots are rewritten from it, so they
 * always match `.metadata`. Slots without a template are never written.
 */
import Logger from "../../clients/sentry";
import { serializeErrorForWarning } from "../../errors";
import { workbookCache } from "./cache";
import { getEstimateReportData, getProjectReportData } from "./index";

export type PrintLayoutSection = "header" | "footer";
export type PrintLayoutPosition = "left" | "center" | "right";

export interface PrintLayoutSlot {
  section: PrintLayoutSection;
  position: PrintLayoutPosition;
}

export type PrintLayoutSaveResult =
  | { outcome: "unsupported" }
  /** The section's text besides the names is too long to leave room for them. */
  | { outcome: "tooLong"; sheetName: string; section: PrintLayoutSection }
  /** The sheet has no token, so the workbook no longer has a template. */
  | { outcome: "cleared"; sheetName: string }
  | { outcome: "saved"; sheetName: string; slots: PrintLayoutSlot[]; failedSheets: string[] };

type SlotProperty = `${PrintLayoutPosition}${"Header" | "Footer"}`;

/** The slots that have a template, by their Excel property name. */
type PrintLayoutTemplate = Partial<Record<SlotProperty, string>>;

const SECTION_SLOTS: Record<PrintLayoutSection, SlotProperty[]> = {
  header: ["leftHeader", "centerHeader", "rightHeader"],
  footer: ["leftFooter", "centerFooter", "rightFooter"],
};

const SLOT_PROPERTIES = [...SECTION_SLOTS.header, ...SECTION_SLOTS.footer];

const SLOTS: Record<SlotProperty, PrintLayoutSlot> = {
  leftHeader: { section: "header", position: "left" },
  centerHeader: { section: "header", position: "center" },
  rightHeader: { section: "header", position: "right" },
  leftFooter: { section: "footer", position: "left" },
  centerFooter: { section: "footer", position: "center" },
  rightFooter: { section: "footer", position: "right" },
};

/**
 * Every token a template may use. Adding one is one entry: the name is what goes between the braces,
 * the resolver returns the value from its `.metadata` row (anything empty prints as nothing).
 */
const PRINT_LAYOUT_TOKENS: Record<string, () => Promise<unknown>> = {
  "project.name": async () => (await getProjectReportData())[0]?.project_name,
  "estimate.name": async () => (await getEstimateReportData())[0]?.estimate_name,
};

const TOKEN_PATTERN = /\{\{\s*([\w.]+)\s*\}\}/g;

/**
 * Excel caps a header or footer at 255 characters, counted in a way we don't rely on. The names get
 * at most half, and Save keeps everything else in the section to the other half.
 */
const NAMES_LIMIT = 127;
const SECTION_TEXT_LIMIT = 128;
/** Excel stores a section as `&L…&C…&R…`. */
const SECTION_MARKERS_LENGTH = 6;

const isPrintLayoutSupported = (): boolean => Office.context.requirements.isSetSupported("ExcelApi", "1.9");

const tokensIn = (text: string): string[] => {
  const names: string[] = [];
  text.replace(TOKEN_PATTERN, (match, name: string) => {
    if (name in PRINT_LAYOUT_TOKENS) names.push(name);
    return match;
  });
  return names;
};

const hasToken = (text: string): boolean => tokensIn(text).length > 0;

const withoutTokens = (text: string): string =>
  text.replace(TOKEN_PATTERN, (match, name: string) => (name in PRINT_LAYOUT_TOKENS ? "" : match));

/** Excel reads `&` as the start of a format code, so a literal one in a value must be doubled. */
const escapeForHeaderFooter = (value: string): string => value.replace(/&/g, "&&");

/** Anything malformed reads as no template, which the next save replaces. */
const parseTemplate = (stored: unknown): PrintLayoutTemplate | null => {
  if (typeof stored !== "object" || stored === null) return null;
  const template: PrintLayoutTemplate = {};
  for (const slot of SLOT_PROPERTIES) {
    const text = (stored as Record<string, unknown>)[slot];
    if (typeof text === "string" && hasToken(text)) template[slot] = text;
  }
  return Object.keys(template).length > 0 ? template : null;
};

const readTemplate = async (): Promise<PrintLayoutTemplate | null> =>
  parseTemplate(await workbookCache.get("printLayout"));

const resolveTokens = async (): Promise<Map<string, string>> => {
  // Sequential, not Promise.all: each resolver opens its own Excel batch, and batches can't overlap.
  const values = new Map<string, string>();
  for (const [name, resolver] of Object.entries(PRINT_LAYOUT_TOKENS)) {
    const value = await resolver();
    values.set(name, value == null ? "" : String(value));
  }
  return values;
};

/** Shortens the longest names, ending them with "…", until every name in the section fits. */
const fitNames = (names: string[], values: Map<string, string>): Map<string, string> => {
  const fitted = new Map(values);
  const length = (name: string) => escapeForHeaderFooter(fitted.get(name) ?? "").length;
  let excess = names.reduce((sum, name) => sum + length(name), 0) - NAMES_LIMIT;
  while (excess > 0) {
    const longest = names.reduce((a, b) => (length(b) > length(a) ? b : a));
    const value = fitted.get(longest) ?? "";
    if (value.length <= 1) break;
    // Cut before escaping, so a doubled `&&` is never split.
    fitted.set(longest, `${value.slice(0, Math.max(0, value.length - excess - 1))}…`);
    excess = names.reduce((sum, name) => sum + length(name), 0) - NAMES_LIMIT;
  }
  return fitted;
};

/** The text each templated slot gets, with the names fitted per section. */
const fillTemplate = (template: PrintLayoutTemplate, values: Map<string, string>): PrintLayoutTemplate => {
  const filled: PrintLayoutTemplate = {};
  for (const slots of Object.values(SECTION_SLOTS)) {
    const fitted = fitNames(
      slots.flatMap((slot) => tokensIn(template[slot] ?? "")),
      values
    );
    for (const slot of slots) {
      const text = template[slot];
      if (text === undefined) continue;
      filled[slot] = text.replace(TOKEN_PATTERN, (match, name: string) =>
        fitted.has(name) ? escapeForHeaderFooter(fitted.get(name) ?? "") : match
      );
    }
  }
  return filled;
};

const writeSlots = (worksheet: Excel.Worksheet, filled: PrintLayoutTemplate): void => {
  const headerFooter = worksheet.pageLayout.headersFooters.defaultForAllPages;
  for (const slot of SLOT_PROPERTIES) {
    const text = filled[slot];
    if (text !== undefined) headerFooter[slot] = text;
  }
};

/**
 * Writes the template to every sheet. A sheet Excel rejects (its own text in the other slots can
 * still push it over the limit) is retried without the names, so it never keeps a stale one.
 * Returns the sheets that could not be written at all.
 */
const writeToEverySheet = async (template: PrintLayoutTemplate, values: Map<string, string>): Promise<string[]> => {
  const filled = fillTemplate(template, values);
  const withoutNames = fillTemplate(template, new Map(Array.from(values.keys(), (name) => [name, ""])));

  return Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets.load("items/name");
    await context.sync();
    try {
      worksheets.items.forEach((worksheet) => writeSlots(worksheet, filled));
      await context.sync();
      return [];
    } catch (error) {
      Logger.warn("Headers and footers rejected; retrying sheet by sheet", serializeErrorForWarning(error));
    }

    const failed: string[] = [];
    for (const worksheet of worksheets.items) {
      try {
        writeSlots(worksheet, filled);
        await context.sync();
      } catch {
        try {
          writeSlots(worksheet, withoutNames);
          await context.sync();
          Logger.warn("Header or footer written without names", { sheet: worksheet.name });
        } catch (error) {
          failed.push(worksheet.name);
          Logger.warn(
            "Header or footer could not be written",
            serializeErrorForWarning(error, { sheet: worksheet.name })
          );
        }
      }
    }
    return failed;
  });
};

/**
 * Rewrites every sheet's templated slots from the current project and estimate report rows. Call it
 * whenever one of those rows is written or cleared. Does nothing in a workbook without a template.
 * Never throws: a failure is logged and the sheets keep what they had.
 */
export const refillPrintLayouts = async (): Promise<void> => {
  if (!isPrintLayoutSupported()) return;
  try {
    const template = await readTemplate();
    if (!template) return;
    await writeToEverySheet(template, await resolveTokens());
  } catch (error) {
    Logger.warn("Refilling headers and footers failed", serializeErrorForWarning(error));
  }
};

/**
 * Makes the active sheet's header and footer the workbook's template, and writes it to every sheet.
 *
 * A slot holding a token is saved as typed. A slot still showing the saved template filled in keeps
 * that template, since a refill replaced its tokens with the names. Any other slot stops being
 * updated; so does the whole workbook when no slot qualifies.
 */
export const savePrintLayout = async (): Promise<PrintLayoutSaveResult> => {
  if (!isPrintLayoutSupported()) return { outcome: "unsupported" };

  const saved = await readTemplate();
  const values = await resolveTokens();
  const savedFilled = saved ? fillTemplate(saved, values) : {};

  const { sheetName, current } = await Excel.run(async (context) => {
    const worksheet = context.workbook.worksheets.getActiveWorksheet().load("name");
    const headerFooter = worksheet.pageLayout.headersFooters.defaultForAllPages.load(SLOT_PROPERTIES);
    await context.sync();
    return { sheetName: worksheet.name, current: headerFooter };
  });
  const textOf = (slot: SlotProperty): string => current[slot] ?? "";

  const template: PrintLayoutTemplate = {};
  for (const slot of SLOT_PROPERTIES) {
    const text = textOf(slot);
    if (hasToken(text)) template[slot] = text;
    else if (saved?.[slot] && text === savedFilled[slot]) template[slot] = saved[slot];
  }

  for (const [section, slots] of Object.entries(SECTION_SLOTS) as [PrintLayoutSection, SlotProperty[]][]) {
    const textLength = slots.reduce((sum, slot) => sum + withoutTokens(template[slot] ?? textOf(slot)).length, 0);
    if (textLength + SECTION_MARKERS_LENGTH > SECTION_TEXT_LIMIT) return { outcome: "tooLong", sheetName, section };
  }

  const slots = SLOT_PROPERTIES.filter((slot) => template[slot] !== undefined);
  if (slots.length === 0) {
    await workbookCache.delete("printLayout");
    return { outcome: "cleared", sheetName };
  }

  await workbookCache.set({ printLayout: template });
  const failedSheets = await writeToEverySheet(template, values);
  return { outcome: "saved", sheetName, slots: slots.map((slot) => SLOTS[slot]), failedSheets };
};
